import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { OnBatchShapesCommitted, OnShapeCommitted } from '../types/effect-control-props'

/**
 * Hand an effect's history items to the right commit callback: one item goes through the
 * single-shape `onShapeCommitted` ('update' source, with its before/after as undo metadata);
 * several go through `onBatchShapesCommitted` as one undo step, each carrying the fresh shape.
 * One item with no `onShapeCommitted` falls through to the batch callback.
 *
 * @returns true when there was anything to commit
 */
export function dispatchShapeCommit(
  items: BatchShapeTransformItem[],
  mgr: ShapeManagerRef | null | undefined,
  onShapeCommitted: OnShapeCommitted | undefined,
  onBatchShapesCommitted: OnBatchShapesCommitted | undefined
): boolean {
  if (items.length === 1 && onShapeCommitted && mgr) {
    const one = items[0]
    const shape = mgr.getShapes().find((x) => x.id === one.shapeId)

    if (shape) {
      onShapeCommitted(shape, 'update', { before: one.before, after: one.after })
    }
  } else if (items.length > 0 && onBatchShapesCommitted && mgr) {
    const fresh = mgr.getShapes()

    onBatchShapesCommitted(
      items.map((i) => ({ ...i, shape: fresh.find((s) => s.id === i.shapeId) })),
      'update'
    )
  }

  return items.length > 0
}
